import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sprout, Home, ClipboardList, PenLine, Inbox, HandHeart, Target, Heart, MessageCircle,
  BookmarkPlus, TrendingUp, Lightbulb, Eye, Lock, Bell, Check, X, ArrowRight, Users,
} from 'lucide-react';

const B = ({ children }) => <strong className="text-foreground font-semibold">{children}</strong>;

const Chip = ({ label, cls }) => (
  <span className={`text-[10px] px-2 py-0.5 rounded border font-mono ${cls}`}>{label}</span>
);

const Good = ({ children }) => (
  <div className="flex items-start gap-2 bg-emerald-500/5 border border-emerald-500/20 rounded-sm px-3 py-2">
    <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
    <p className="text-[12px] text-foreground/80 leading-relaxed">{children}</p>
  </div>
);

const Bad = ({ children }) => (
  <div className="flex items-start gap-2 bg-secondary/40 border border-border rounded-sm px-3 py-2">
    <X className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
    <p className="text-[12px] text-muted-foreground leading-relaxed line-through decoration-muted-foreground/40">{children}</p>
  </div>
);

function Section({ icon: Icon, title, color, children, testid }) {
  return (
    <div className="bg-card border border-border rounded-sm p-5" data-testid={testid}>
      <h2 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-3">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center border ${color}`}>
          <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
        </span>
        {title}
      </h2>
      <div className="text-[13px] text-foreground/80 leading-relaxed space-y-2.5">{children}</div>
    </div>
  );
}

export default function FeedbackGuidePage() {
  const navigate = useNavigate();
  return (
    <div className="max-w-3xl space-y-5 animate-fade-in-up pb-8" data-testid="feedback-guide-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight flex items-center gap-2.5">
          <Sprout className="w-7 h-7 text-emerald-400" /> Feedback Guide
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Wie unser Team-Development-Modul funktioniert — und wie wir bei OLTA Feedback geben, das wirklich hilft.
        </p>
      </div>

      <div className="bg-emerald-500/5 border border-emerald-500/25 rounded-sm p-5 text-center" data-testid="guide-leitgedanke">
        <p className="text-sm text-foreground italic leading-relaxed">
          „Hier will dir niemand etwas Böses. Wir helfen einander, besser zu werden — das ist alles.“
        </p>
        <p className="text-[11px] text-muted-foreground mt-1.5">Der Leitgedanke hinter allem, was in diesem Modul passiert.</p>
      </div>

      <Section icon={Heart} title="Die 5 Grundsätze" color="border-emerald-500/40 bg-emerald-500/10 text-emerald-400" testid="guide-principles">
        <ul className="space-y-2">
          <li><B>1. Immer mit Namen, nie anonym.</B> Wir stehen zu dem, was wir sagen — das schafft Vertrauen.</li>
          <li><B>2. Keine Noten, keine Rankings.</B> Nur Text und positive Signale. Ein Mensch ist keine Zahl.</li>
          <li><B>3. Kritik nur mit Kontext.</B> Beobachtungen laufen immer über das SBI-Format — niemals per Ein-Klick.</li>
          <li><B>4. Feedback gehört dem Empfänger.</B> Privat by default. Auch Admins haben keinen Zugriff — das ist technisch im Backend erzwungen, nicht nur versteckt.</li>
          <li><B>5. Ziele gehören dem Owner.</B> Niemand kann dir ein Ziel anlegen — nur du formulierst deine Ambitionen.</li>
        </ul>
      </Section>

      <Section icon={PenLine} title="Das SBI-Format: Situation → Verhalten → Wirkung" color="border-violet-500/40 bg-violet-500/10 text-violet-400" testid="guide-sbi">
        <p>Jedes Feedback hat drei Bausteine. Zusammen machen sie es konkret, fair und veränderbar:</p>
        <div className="grid sm:grid-cols-3 gap-2 my-2">
          <div className="border border-sky-500/25 bg-sky-500/5 rounded-sm p-3">
            <p className="text-[10px] font-mono uppercase text-sky-400 mb-1">Situation</p>
            <p className="text-[12px]">Wann und wo war es? Der konkrete Moment.</p>
          </div>
          <div className="border border-violet-500/25 bg-violet-500/5 rounded-sm p-3">
            <p className="text-[10px] font-mono uppercase text-violet-400 mb-1">Verhalten</p>
            <p className="text-[12px]">Was hast du beobachtet? Beschreiben, nicht interpretieren.</p>
          </div>
          <div className="border border-amber-500/25 bg-amber-500/5 rounded-sm p-3">
            <p className="text-[10px] font-mono uppercase text-amber-400 mb-1">Wirkung</p>
            <p className="text-[12px]">Was hat es bei dir ausgelöst? Deine Wahrnehmung ist unbestreitbar wahr.</p>
          </div>
        </div>
        <Good>„Im Verkäufergespräch am Dienstag (Situation) hast du dreimal unterbrochen, bevor der Verkäufer fertig war (Verhalten). Auf mich wirkte es gehetzt, der Verkäufer wurde einsilbig (Wirkung).“</Good>
        <Bad>„Du bist ungeduldig.“ — Eine Charakterdiagnose kann man nicht ändern. Ein Verhalten schon.</Bad>
        <p className="text-[12px] text-muted-foreground">Optional gibt es ein viertes Feld: <B>Feedforward</B> — „Was könntest du beim nächsten Mal ausprobieren?“ Zukunft statt Vergangenheit.</p>
        <p className="text-[12px]">Vor dem Absenden fragt das System: <B>„Würdest du das der Person genauso ins Gesicht sagen?“</B> Falls nein — kurz umformulieren. Falls ja — raus damit.</p>
      </Section>

      <Section icon={Home} title="Home: To-dos, Team-Feed & Ziel-Leiste" color="border-sky-500/40 bg-sky-500/10 text-sky-400" testid="guide-home">
        <p>Dein Startpunkt. Oben siehst du deine <B>To-dos</B> (offener Check-in, wartende Feedback-Wünsche, Ideen ohne Reaktion). Darunter der <B>Team-Feed</B>: alle Wochen-Highlights 🌟 und Kudos 💛 — bewusst nur Positives. Die <B>Ziel-Leiste</B> zeigt kompakt, woran das Team gerade wächst.</p>
      </Section>

      <Section icon={ClipboardList} title="Der Wochen-Check-in (unter 5 Minuten)" color="border-amber-500/40 bg-amber-500/10 text-amber-400" testid="guide-checkin">
        <ul className="space-y-1.5">
          <li><B>Highlight (Pflicht):</B> Was lief gut? Landet im Team-Feed. Konkret schlägt allgemein.</li>
          <li><B>@-Erwähnung = Kudos:</B> Schreibst du <B>@Max</B> ins Highlight, bekommt Max automatisch ein Kudos mit Push — sichtbar im Feed. 💛</li>
          <li><B>Blocker (optional):</B> Was hat dich gebremst? Sichtbar fürs Team. Beschreibe das Problem, nicht den Schuldigen.</li>
          <li><B>Ziel-Status (1 Tap pro Ziel):</B> „Fortschritt gemacht“ / „Keine Gelegenheit“ / „War schwieriger“ — bei Letzterem kannst du eine private Notiz für deinen Rückblick ergänzen.</li>
        </ul>
        <p className="text-[12px] text-muted-foreground">Dein Entwurf wird automatisch gespeichert. Reminder kommt donnerstags um 10:00 — Deadline ist Freitag 17:00.</p>
      </Section>

      <Section icon={Inbox} title="Postfach: dein privater Raum" color="border-emerald-500/40 bg-emerald-500/10 text-emerald-400" testid="guide-inbox">
        <p>Über jedem erhaltenen Feedback steht: <B>„[Name] hat sich Zeit genommen, dir beim Besserwerden zu helfen.“</B> Feedback ist ein Geschenk von jemandem, der es gut meint.</p>
        <p>Drei Aktionen pro Feedback:</p>
        <ul className="space-y-1.5">
          <li className="flex items-start gap-2"><Heart className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" /><span><B>Danke sagen</B> — ein Klick, der beim Absender ankommt.</span></li>
          <li className="flex items-start gap-2"><MessageCircle className="w-3.5 h-3.5 text-sky-400 mt-0.5 shrink-0" /><span><B>Nachfrage stellen</B> — privater Thread nur zwischen euch beiden. Nachfragen ja, rechtfertigen nein: „Kannst du mir ein zweites Beispiel geben?“ bringt dich weiter als „Ja, aber …“.</span></li>
          <li className="flex items-start gap-2"><BookmarkPlus className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /><span><B>Das nehme ich mit</B> — ein Satz Erkenntnis, optional mit einem deiner Ziele verknüpft. So wird Feedback zu Wachstum.</span></li>
        </ul>
        <p>Hier landen auch <B>Ideen zu deinen Zielen</B> — mit drei Reaktionen: 🚀 Probier ich aus · 🕐 Interessant, später · 🙏 Passt für mich nicht. Der Ideengeber sieht nur, <B>dass</B> du reagiert hast — nie wie. Bei 🚀 fragt dich der nächste Check-in, wie es lief.</p>
      </Section>

      <Section icon={HandHeart} title="Feedback anfordern: der beste Move" color="border-violet-500/40 bg-violet-500/10 text-violet-400" testid="guide-requests">
        <p>Erbetenes Feedback ist das wertvollste. Zwei Typen:</p>
        <div className="flex gap-2 flex-wrap my-1">
          <Chip label="🔍 Beobachtung — Wie wirke ich?" cls="text-sky-400 bg-sky-400/10 border-sky-500/30" />
          <Chip label="💡 Ideen — Was würdet ihr tun?" cls="text-violet-400 bg-violet-400/10 border-violet-500/30" />
        </div>
        <p>Stelle eine <B>konkrete Frage</B> („Wie war mein Teil im Mentoren-Call gestern?“ schlägt „Feedback zu mir allgemein“), wähle <B>1–2 Personen</B>, Frist ist eine Woche (Reminder 1 Tag vorher). Die Angefragten sehen deine Frage als Kontext über ihrem SBI-Formular — Ablehnen ist völlig okay und braucht keine Begründung.</p>
      </Section>

      <Section icon={Target} title="Zielkarten: Ambitionen statt Schwächen" color="border-emerald-500/40 bg-emerald-500/10 text-emerald-400" testid="guide-goals">
        <p>Du formulierst <B>max. 2 aktive Ziele</B> — als Zielzustand, nicht als Problem:</p>
        <Good>„Modelle verständlich und selbsterklärend aufbereiten“</Good>
        <Bad>„Unübersichtliche Modelle abstellen“</Bad>
        <p>Jedes Ziel trägt einen Tag: <Chip label="Hard Skill (Fachliches)" cls="text-sky-400 bg-sky-400/10 border-sky-500/30" /> oder <Chip label="Soft Skill (Wirkung & Zusammenarbeit)" cls="text-emerald-400 bg-emerald-400/10 border-emerald-500/30" />. Die private Herkunfts-Notiz („Woher kommt dieses Ziel?“) siehst nur du.</p>
        <p>Statt Fortschrittsbalken wächst eine <B>Pflanze: 🌱 → 🌿 → 🌳</B> — mit jedem Signal und jeder Idee vom Team. Eine junge Pflanze ist nicht „unvollständig“, sie ist einfach nur jung.</p>
        <p>Auf den Karten der anderen hast du drei Aktionen:</p>
        <ul className="space-y-1.5">
          <li className="flex items-start gap-2"><TrendingUp className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /><span><B>📈 Fortschritt gesehen!</B> — ein konkreter Einzeiler: WAS hat funktioniert, damit die Person es wiederholen kann.</span></li>
          <li className="flex items-start gap-2"><Lightbulb className="w-3.5 h-3.5 text-violet-400 mt-0.5 shrink-0" /><span><B>💡 Idee dazu</B> — „Probier mal X“ statt „Du machst immer Y“. Eine Idee ist ein Angebot; der Owner entscheidet privat.</span></li>
          <li className="flex items-start gap-2"><Eye className="w-3.5 h-3.5 text-sky-400 mt-0.5 shrink-0" /><span><B>Beobachtung</B> — für alles, was kein Fortschritt ist: öffnet das SBI-Formular, angedockt ans Ziel, geht privat an den Owner.</span></li>
        </ul>
        <p>Beim <B>Abschluss</B> gibt es Konfetti 🎉 und du teilst öffentlich „Was hat geholfen?“ — so lernt das ganze Team mit.</p>
      </Section>

      <Section icon={MessageCircle} title="Gespräche & Telefonate: Feedback offline" color="border-amber-500/40 bg-amber-500/10 text-amber-400" testid="guide-conversations">
        <p>Nicht jedes Feedback kommt digital — manches kommt am Telefon oder am Tisch (z. B. von Rüdiger). Damit es nicht verloren geht:</p>
        <ul className="space-y-1.5">
          <li><B>🗣 Gespräch festhalten</B> (im Postfach): Nach dem Gespräch hältst <B>du als Empfänger</B> das Feedback selbst fest — Schritt für Schritt durch die SBI-Fragen geführt (Leitfaden-Modus).</li>
          <li><B>Quelle-Tag:</B> 📞 Telefonat, 🤝 Persönlich oder 💻 Video — so weißt du später, woher es kam.</li>
          <li><B>Gesprächspartner:</B> aus der Userliste wählen oder einfach den Namen tippen (falls die Person noch keinen Account hat).</li>
          <li>Die Notiz landet in deinem <B>privaten Postfach</B> wie normales Feedback — Takeaways und Ziel-Andocken funktionieren genauso. Dein Gesprächspartner sieht nichts davon.</li>
        </ul>
        <p className="text-[12px] text-muted-foreground">Tipp: Nutze die SBI-Fragen im Dialog schon <B>während</B> des Telefonats als Leitfaden — dann fragst du automatisch nach den richtigen Dingen.</p>
      </Section>

      <Section icon={Users} title="Die Monats-Runde: einmal im Monat zusammenkommen" color="border-emerald-500/40 bg-emerald-500/10 text-emerald-400" testid="guide-monthly">
        <p>Der wöchentliche Check-in ist schriftlich — die <B>Monats-Runde</B> ist unser Face-to-Face-Moment. Ein Admin startet sie, alle freigeschalteten Teammitglieder sind dabei. Die Agenda entsteht <B>automatisch aus eurem Monat</B>:</p>
        <ul className="space-y-1.5">
          <li><B>1 · Rückblick:</B> alle Highlights 🌟 und Kudos 💛 des Monats.</li>
          <li><B>2 · Zielkarten-Runde:</B> jede:r erzählt 2 Minuten zum eigenen Ziel — das Team kann <B>live</B> 📈 Signale und 💡 Ideen erfassen, direkt vom Handy.</li>
          <li><B>3 · Erfolge:</B> abgeschlossene Ziele mit den Learnings.</li>
          <li><B>4 · Blocker:</B> wo können wir als Team helfen?</li>
          <li><B>Zum Schluss:</B> jede:r notiert einen privaten Takeaway, der Admin hält Team-Beschlüsse fest und schließt die Runde.</li>
        </ul>
        <p className="text-[12px] text-muted-foreground">Am Monatsanfang erinnert das System die Admins automatisch, die Runde zu planen.</p>
      </Section>

      <Section icon={Lock} title="Wer sieht was? Die Privatsphäre-Matrix" color="border-sky-500/40 bg-sky-500/10 text-sky-400" testid="guide-privacy">
        <div className="space-y-1.5 text-[12px]">
          {[
            ['Erhaltenes Feedback & Threads', 'Nur Absender + Empfänger — auch keine Admins'],
            ['Gesprächsnotizen (Telefonat / persönlich)', 'Nur du — dein Gesprächspartner sieht nichts'],
            ['Takeaways (Feedback & Monats-Runde)', 'Nur du'],
            ['Herkunfts-Notiz eines Ziels', 'Nur der Ziel-Owner'],
            ['Reaktion auf eine Idee (🚀/🕐/🙏)', 'Nur der Owner — der Autor sieht nur DASS reagiert wurde'],
            ['Private Ziel-Notizen („War schwieriger“)', 'Nur der Owner'],
            ['Highlight & Kudos', 'Ganzes Team (Feed)'],
            ['Blocker', 'Ganzes Team'],
            ['Zielkarten (Titel, Skill, Momentum, Trend)', 'Ganzes Team'],
            ['Abschluss-Learnings („Was hat geholfen?“)', 'Ganzes Team — bewusst öffentlich'],
          ].map(([k, v], i) => (
            <div key={i} className="flex items-start gap-2 flex-wrap">
              <ArrowRight className="w-3 h-3 text-muted-foreground/50 mt-1 shrink-0" />
              <span className="text-foreground/90 font-medium min-w-[220px] flex-1">{k}</span>
              <span className="text-muted-foreground flex-1 min-w-[200px]">{v}</span>
            </div>
          ))}
        </div>
        <p className="text-[12px] text-muted-foreground mt-2">Es gibt keine Aggregation über Personen hinweg — keine Vergleiche, keine Summen, keine Rankings.</p>
      </Section>

      <Section icon={Bell} title="Wann meldet sich das System?" color="border-amber-500/40 bg-amber-500/10 text-amber-400" testid="guide-notifications">
        <ul className="space-y-1.5 text-[12px]">
          <li><B>Donnerstag 10:00:</B> sanfter Check-in-Reminder (nur wenn noch offen).</li>
          <li><B>Sofort:</B> Feedback / Idee / Kudos / Anfrage erhalten — aber immer ohne Inhalt: „Du hast Feedback von [Name]“. Gelesen wird nur in der App.</li>
          <li><B>1 Tag vor Frist:</B> Erinnerung an offene Feedback-Wünsche.</li>
        </ul>
        <p className="text-[12px] text-muted-foreground">Keine Streaks, keine Badges, keine Gamification-Punkte — Wachstum braucht keinen Highscore.</p>
      </Section>

      <div className="bg-card border border-border rounded-sm p-5 text-center" data-testid="guide-cta">
        <p className="text-sm text-foreground">Bereit? Der beste Einstieg ist dein erster Check-in — oder eine konkrete Feedback-Anfrage.</p>
        <button onClick={() => navigate('/team-development')} data-testid="guide-goto-teamdev"
          className="mt-3 text-xs font-medium text-emerald-400 border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/15 rounded-full px-5 py-2 transition-colors">
          Zu Team Development →
        </button>
      </div>
    </div>
  );
}
